import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { LogIn } from "lucide-react";

const LOGO = "/logo-aae.png";
const LOGO_WHITE = "/logo-aae-white.webp";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden">
        {/* Halos supplémentaires locaux pour intensifier le côté gauche */}
        <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsla(68, 96%, 63%, 0.35) 0%, transparent 70%)" }} />
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsla(68, 96%, 63%, 0.25) 0%, transparent 70%)" }} />

        <div className="relative h-full w-full p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_WHITE} alt="Antoine Événements" className="h-28 w-auto drop-shadow-[0_6px_24px_rgba(213,245,26,0.35)]" />
          </div>
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider shadow-glow">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" /> Espace de travail équipe AAE
            </div>
            <h1 className="font-brand text-7xl xl:text-8xl leading-[0.95] tracking-tight brand-glow">
              <span className="text-primary">COMPAGNON</span>
            </h1>
          </div>
          <div />
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <Card className="w-full max-w-md border-border/60 shadow-warm-lg">
          <CardContent className="p-8">
            <div className="mb-8 lg:hidden">
              <img src={LOGO} alt="Antoine Événements" className="h-12 w-auto" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">Connexion</div>
            <h2 className="font-brand text-3xl tracking-tight text-foreground">Bonjour</h2>
            <p className="text-muted-foreground text-sm mt-1 mb-8">Accéder à votre espace en ligne</p>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email professionnel</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom@antoineevenements.fr"
                  data-testid="login-email-input"
                  className="h-11 bg-muted focus:bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-11 bg-muted focus:bg-card"
                />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-full font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 active:scale-95 shadow-glow"
                data-testid="login-submit-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-6 text-center">
              Contactez votre administrateur si vous n'avez pas encore de compte.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
